export type EctdLeafInfo = {
  parentName: string;
  title?: string;
};

export type EctdLeafMap = Record<string, EctdLeafInfo>;

export function normalizeEctdPath(path: string): string {
  return path
    .replace(/\\/g, "/")
    .replace(/^\.\/+/, "")
    .toLowerCase();
}

function resolveRelativePath(basePath: string, relative: string): string {
  const normalizedRelative = relative.replace(/\\/g, "/");

  // If already absolute (starts at root), return as-is
  if (normalizedRelative.startsWith("/")) {
    return normalizedRelative.slice(1);
  }

  const baseParts = basePath.replace(/\\/g, "/").split("/");
  // Remove the file name part to get the base directory
  baseParts.pop();

  const relParts = normalizedRelative.split("/");

  relParts.forEach((part) => {
    if (!part || part === ".") return;
    if (part === "..") {
      if (baseParts.length > 0) baseParts.pop();
    } else {
      baseParts.push(part);
    }
  });

  return baseParts.join("/");
}

function bytesToString(bytes: Uint8Array): string {
  // Prefer proper UTF-8 decoding and strip any BOM if present.
  try {
    if (typeof TextDecoder !== "undefined") {
      const decoded = new TextDecoder("utf-8").decode(bytes);
      return decoded.replace(/^\uFEFF/, "");
    }
  } catch {
    // Fallback to manual decoding below if TextDecoder is not available.
  }

  let result = "";
  for (let i = 0; i < bytes.length; i += 1) {
    result += String.fromCharCode(bytes[i]);
  }
  // Strip UTF-8 BOM that may appear as U+FEFF or as ï»¿ when mis-decoded.
  return result.replace(/^(?:\uFEFF|ï»¿)/, "");
}

export function buildEctdLeafMapFromEntries(
  entries: Record<string, Uint8Array>
): EctdLeafMap {
  const map: EctdLeafMap = {};

  const xmlEntryNames = Object.keys(entries).filter((name) =>
    name.toLowerCase().endsWith("index.xml")
  );

  if (xmlEntryNames.length === 0) return map;

  const visitedXml = new Set<string>();
  const parser = new DOMParser();

  const findEntryByNormalizedPath = (
    normalizedPath: string
  ): string | undefined =>
    Object.keys(entries).find(
      (name) => normalizeEctdPath(name) === normalizedPath
    );

  const traverseXml = (entryName: string): void => {
    const normalizedEntryName = normalizeEctdPath(entryName);
    // console.log("normalizedEntryName", normalizedEntryName);
    if (visitedXml.has(normalizedEntryName)) return;
    visitedXml.add(normalizedEntryName);

    const bytes = entries[entryName];
    if (!bytes) return;

    const xmlText = bytesToString(bytes);
    const doc = parser.parseFromString(xmlText, "text/xml");
    const leafElements = Array.from(doc.getElementsByTagName("leaf"));
    leafElements.forEach((leaf) => {
      const href = leaf.getAttribute("xlink:href") || leaf.getAttribute("href");
      if (!href) return;

      const targetPath = resolveRelativePath(entryName, href);
      const normalizedTarget = normalizeEctdPath(targetPath);

      const parent = leaf.parentElement;
      const keyName = parent?.tagName;
      if (!keyName) return;

      const titleElement = leaf.getElementsByTagName("title")[0];
      const title = titleElement?.textContent?.trim() || undefined;

      const lowerHref = href.toLowerCase();
      if (lowerHref.endsWith(".xml")) {
        const xmlEntry = findEntryByNormalizedPath(normalizedTarget);
        if (xmlEntry) {
          traverseXml(xmlEntry);
        }
        return;
      }

      if (lowerHref.endsWith(".pdf")) {
        if (!map[normalizedTarget]) {
          map[normalizedTarget] = { parentName: keyName, title };
        }
      }
    });
  };

  xmlEntryNames.forEach(traverseXml);

  return map;
}
